import type { DateRange, MonthSeries, SeriesSet } from '../types/dashboard';
import { perUnitRange, rangeMetrics } from './metrics';
import { change, changeKind, formatValue, isCountMetric, metricMeta, monthValue, rangeValue, type TrendMetric } from './metricConfig';
import { monthLabel } from './calendar';
import { fmtInt, fmtPct } from './format';

export interface KpiDelta {
  value: number | null;
  kind: 'pct' | 'pp';
  label: string;
}

export interface KpiItem {
  id: string;
  label: string;
  value: string;
  deltas: KpiDelta[];
  caption: string;
  definitionId: string;
  emphasis?: boolean;
}

export interface KpiInput {
  metric: TrendMetric;
  series: SeriesSet;
  network: SeriesSet;
  range: DateRange;
  lyRange: DateRange | null;
  scopeUnits: (m: number) => number;
  networkUnits: (m: number) => number;
  unitLabel: 'territory' | 'AOP';
  single: boolean;
  scopeLabel: string;
  below?: { count: number; total: number; noun: string };
}

const PERIOD_LABEL: Record<TrendMetric, string> = {
  members: 'New Members in Selected Period',
  registrations: 'Registrations in Selected Period',
  revenue: 'Revenue in Selected Period (illustrative)',
  conversion: 'Conversion Rate · Selected Period',
  regsPerMember: 'Regs per Member · Selected Period',
};

const MONTH_LABEL: Record<TrendMetric, string> = {
  members: 'New Members',
  registrations: 'Registrations',
  revenue: 'Revenue',
  conversion: 'Conversion Rate',
  regsPerMember: 'Regs per Member',
};

/** Monthly value per active unit (null when nothing is active that month). */
export function perUnitMonth(metric: TrendMetric, s: MonthSeries, m: number, units: (m: number) => number): number | null {
  const v = monthValue(metric, s, m);
  const u = m >= 0 ? units(m) : 0;
  return v === null || !u ? null : v / u;
}

/**
 * KPI cards. Period cards compare to the same period one year earlier.
 * Month cards use the latest completed month in the range and compare
 * to the prior month (MoM) and the same month last year (YoY).
 */
export function buildMetricKpis(i: KpiInput): KpiItem[] {
  const { metric, series, network, range, lyRange } = i;
  const meta = metricMeta(metric);
  const kind = changeKind(metric);
  const fmt = (v: number) => formatValue(metric, v);
  const count = isCountMetric(metric);
  const m = range.end;
  const mLbl = monthLabel(m);
  const prevLbl = monthLabel(m - 1);
  const lyLbl = monthLabel(m - 12);

  const period = rangeMetrics(series.all, range.start, range.end);
  const cur = rangeValue(metric, period);
  const lyPeriod = lyRange ? rangeMetrics(series.all, lyRange.start, lyRange.end) : null;
  const ly = lyPeriod ? rangeValue(metric, lyPeriod) : null;
  const last = monthValue(metric, series.all, m);
  const items: KpiItem[] = [];

  const periodCaption =
    metric === 'conversion'
      ? `${fmtInt(period.registeringMembers)} converted of ${fmtInt(period.members)} new members · ${range.label}`
      : metric === 'regsPerMember'
      ? `${fmtInt(period.registrations)} regs from ${fmtInt(period.registeringMembers)} members · ${range.label}`
      : `${i.scopeLabel} · ${range.label}`;

  items.push({
    id: 'total',
    label: PERIOD_LABEL[metric],
    value: fmt(cur),
    emphasis: true,
    deltas: [{ value: change(metric, cur, ly), kind, label: lyRange ? `YoY vs ${lyRange.label}` : 'YoY' }],
    caption: periodCaption,
    definitionId: meta.definitionId,
  });

  items.push({
    id: 'latest',
    label: `${MONTH_LABEL[metric]} · ${mLbl}`,
    value: last === null ? 'n/a' : fmt(last),
    deltas: [
      { value: change(metric, last, monthValue(metric, series.all, m - 1)), kind, label: `MoM vs ${prevLbl}` },
      { value: change(metric, last, monthValue(metric, series.all, m - 12)), kind, label: `YoY vs ${lyLbl}` },
    ],
    caption: 'Latest completed month in range',
    definitionId: meta.definitionId,
  });

  if (count) {
    const netAvg = perUnitMonth(metric, network.all, m, i.networkUnits);
    const netUnits = i.networkUnits(m);
    const netTotal = monthValue(metric, network.all, m);
    if (i.single) {
      items.push({
        id: 'avg',
        label: `Network Avg per Active ${i.unitLabel === 'AOP' ? 'AOP' : 'Territory'} · ${mLbl}`,
        value: netAvg === null ? 'n/a' : fmt(netAvg),
        deltas: [{ value: change(metric, last, netAvg), kind, label: `this ${i.unitLabel} vs avg` }],
        caption: netTotal === null ? 'No active units' : `${fmt(netTotal)} ÷ ${netUnits} active ${i.unitLabel === 'AOP' ? 'AOPs' : 'territories'}`,
        definitionId: 'networkAverage',
      });
    } else {
      const avg = perUnitMonth(metric, series.all, m, i.scopeUnits);
      const prevAvg = perUnitMonth(metric, series.all, m - 1, i.scopeUnits);
      items.push({
        id: 'avg',
        label: `Avg per Active Territory · ${mLbl}`,
        value: avg === null ? 'n/a' : fmt(avg),
        deltas: [
          { value: change(metric, avg, prevAvg), kind, label: `MoM vs ${prevLbl}` },
          ...(i.scopeUnits(m) !== netUnits ? [{ value: change(metric, avg, netAvg), kind, label: 'vs network avg' } as KpiDelta] : []),
        ],
        caption: last === null ? 'No active territories' : `${fmt(last)} ÷ ${i.scopeUnits(m)} active territories`,
        definitionId: 'networkAverage',
      });
    }
  } else {
    const netPeriod = rangeValue(metric, perUnitRange(network.all, range.start, range.end, i.networkUnits));
    items.push({
      id: 'avg',
      label: 'Network · Selected Period',
      value: fmt(netPeriod),
      deltas: [{ value: change(metric, cur, netPeriod), kind, label: 'selected scope vs network' }],
      caption: 'All active territories, same filters',
      definitionId: 'networkAverage',
    });
  }

  const dRm = rangeMetrics(series.Digital, range.start, range.end);
  const lRm = rangeMetrics(series.Local, range.start, range.end);
  const uRm = rangeMetrics(series.Unknown, range.start, range.end);
  const d = rangeValue(metric, dRm);
  const l = rangeValue(metric, lRm);
  const u = rangeValue(metric, uRm);
  if (count) {
    const total = d + l + u;
    items.push({
      id: 'source',
      label: 'Digital Share · Selected Period',
      value: total ? fmtPct(d / total, 0) : 'n/a',
      deltas: lyRange
        ? [
            { value: change(metric, d, rangeValue(metric, rangeMetrics(series.Digital, lyRange.start, lyRange.end))), kind, label: 'Digital YoY' },
            { value: change(metric, l, rangeValue(metric, rangeMetrics(series.Local, lyRange.start, lyRange.end))), kind, label: 'Local / Other YoY' },
          ]
        : [],
      caption: `Digital ${fmt(d)} · Local / Other ${fmt(l)} · Unknown ${fmt(u)} (${total ? fmtPct(u / total, 0) : '—'}) · illustrative mapping`,
      definitionId: 'acquisitionSource',
    });
  } else {
    items.push({
      id: 'source',
      label: 'Digital vs Local / Other · Selected Period',
      value: `${dRm.members ? fmt(d) : '—'} / ${lRm.members ? fmt(l) : '—'}`,
      deltas: [{ value: dRm.members && lRm.members ? change(metric, d, l) : null, kind, label: 'Digital vs Local / Other' }],
      caption: `Unknown / Unclassified ${uRm.members ? fmt(u) : '—'} · by member referrer · illustrative`,
      definitionId: 'acquisitionSource',
    });
  }

  if (i.below) {
    items.push({
      id: 'below',
      label: `${i.below.noun} Below Network`,
      value: `${i.below.count} of ${i.below.total}`,
      deltas: [],
      caption: `${meta.label} · monthly avg vs network monthly avg`,
      definitionId: 'performanceStatus',
    });
  }
  return items;
}
